import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrashIcon } from '../../../assets';
import { ACTION_TYPES } from '../../../config/constants';
import { Confirm } from '../../../context/confirm/confirm';
import type { TInfSettingsAction } from '../../../types/action';
import { GenericDropdown } from '../../inputs/GenericDropdown';
import { MoneyInput } from '../../inputs/MoneyInput';
import PlatformDropdown from '../../inputs/PlatformDropdown';
import { TextInput } from '../../inputs/TextInput';

/**
 * Proposal-specific action row (packs/pricing). Unlike the shared
 * campaigns/settings/influencer/ActionSettings, this variant replaces the
 * validation toggles with a Precio field locked to the proposal/campaign
 * currency — it is only used inside the proposal "Ajustes Influencer" tab.
 */
export default function ProposalActionSettings({
  action,
  updateAction,
  deleteAction,
  control,
  index,
  fieldPathPrefix = 'actions',
  campaignCurrency = '€',
}: {
  action: TInfSettingsAction;
  updateAction: (key: string, val: any) => void;
  deleteAction: () => void;
  control: any;
  index: number;
  fieldPathPrefix?: string;
  campaignCurrency?: string;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-row items-start gap-4 bg-dark-blue-popups rounded-[8px] p-3">
      <Confirm
        open={open}
        onConfirm={() => deleteAction()}
        setOpen={setOpen}
        type="delete"
        title={t('global.deleteTitle')}
        subtitle={t('global.deleteSubtitle')}
        confirmText={t('global.delete')}
        cancelText={t('global.cancel')}
      />

      <div className="flex flex-col size-full gap-1.5">
        <div className="flex lg:flex-row flex-col gap-1.5">
          <PlatformDropdown
            disabled={action.disabled}
            platformSelected={action.platform}
            onChange={(val) => {
              updateAction('platform', val);
            }}
          />
          <TextInput
            disabled={action.disabled}
            type="number"
            placeholder={t('singleCampaign.numberOfPieces')}
            selectedValue={action.number_of_action}
            extraClassname="bg-white border-none"
            onBlur={(e) => {
              updateAction('number_of_action', Number(e.target.value));
            }}
          />
          <GenericDropdown
            control={control}
            options={ACTION_TYPES[action.platform].map((type: string) => ({
              value: type,
              label: t(`actionType.${type}`),
            }))}
            height="h-[41px]"
            className="!bg-white"
            name={`${fieldPathPrefix}.${index}.type`}
            onChangeManual={(value) => {
              updateAction('type', value.value);
            }}
          />
          <MoneyInput
            control={control}
            name={`${fieldPathPrefix}.${index}.total_amount`}
            registerName={`${fieldPathPrefix}.${index}.total_currency`}
            currentCurrency={campaignCurrency}
            disabled={action.disabled}
            placeholder={t('singleCampaign.precio')}
          />
        </div>

        <TextInput
          type="text"
          placeholder={t('singleCampaign.descriptionActionPlaceholder')}
          maxLength={500}
          extraClassname="bg-white border-none"
          selectedValue={action.description}
          onBlur={(e) => {
            updateAction('description', e.target.value);
          }}
        />
      </div>
      <div
        className={`flex lg:items-center lg:justify-center lg:mt-0 mt-4 cursor-pointer text-red-status hover:text-influentia-red`}
        onClick={() => setOpen(true)}
      >
        <TrashIcon className="size-[15px]" />
      </div>
    </div>
  );
}
